import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { EmptyGridCell, GridCellWrap } from '../../../components/shared/agGridCells';
import { Tooltip, TooltipContent, TooltipTrigger, TOOLTIP_HINT_CLASS } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { BsArrowCounterclockwise, BsCheckLg } from 'react-icons/bs';
import type { ErrorCatalogRow } from '../models/types';
import { CATEGORY_TONE, SEVERITY_TONE } from '../constants/constants';
import { fmtDate, fmtHits, titleCase } from '../utils/utils';

export type ErrorCatalogGridContext = {
  drafts: Record<string, string>;
  savingKey: string | null;
  isDirty: (row: ErrorCatalogRow) => boolean;
  onDraftChange: (errorCode: string, value: string) => void;
  onSaveRow: (row: ErrorCatalogRow) => void;
  onResetRow: (row: ErrorCatalogRow) => void;
};

function ErrorCodeCellRenderer(params: ICellRendererParams<ErrorCatalogRow>) {
  const row = params.data;
  if (!row) return <EmptyGridCell />;
  const isWildcard = row.errorCode === '*';

  if (isWildcard) {
    return (
      <GridCellWrap>
        <span className="text-[13px] italic text-muted-foreground">
          Any other error — fallback
        </span>
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block cursor-default truncate font-mono text-[13px] text-foreground">
            {row.errorCode}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className={cn(TOOLTIP_HINT_CLASS, "font-mono")}>
          {row.errorCode}
        </TooltipContent>
      </Tooltip>
    </GridCellWrap>
  );
}

function CategoryCellRenderer(params: ICellRendererParams<ErrorCatalogRow>) {
  const row = params.data;
  if (!row) return <EmptyGridCell />;
  const tone = CATEGORY_TONE[row.category] ?? CATEGORY_TONE.GENERAL;
  return (
    <GridCellWrap>
      <span
        className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
        style={{ backgroundColor: tone.bg, color: tone.color }}
      >
        {row.category}
      </span>
    </GridCellWrap>
  );
}

function SeverityCellRenderer(params: ICellRendererParams<ErrorCatalogRow>) {
  const row = params.data;
  if (!row) return <EmptyGridCell />;
  const tone = SEVERITY_TONE[row.severity] ?? SEVERITY_TONE.MEDIUM;
  return (
    <GridCellWrap>
      <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: tone.text }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone.dot }} />
        {titleCase(row.severity)}
      </span>
    </GridCellWrap>
  );
}

function MessageEditorCellRenderer(params: ICellRendererParams<ErrorCatalogRow>) {
  const row = params.data;
  const context = params.context as ErrorCatalogGridContext | undefined;
  if (!row || !context) return <EmptyGridCell />;

  const value = context.drafts[row.errorCode] ?? '';
  const dirty = context.isDirty(row);

  return (
    <GridCellWrap>
      <div className="flex w-full items-center gap-1.5">
        <input
          value={value}
          onChange={(e) => context.onDraftChange(row.errorCode, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') context.onSaveRow(row);
            if (e.key === 'Escape') context.onResetRow(row);
          }}
          placeholder="Add a message…"
          className="h-8 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-sm text-foreground placeholder:text-muted-foreground/60 hover:border-input focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {dirty && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              title="Save"
              disabled={context.savingKey === row.errorCode}
              onClick={() => context.onSaveRow(row)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
            >
              <BsCheckLg className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Reset"
              onClick={() => context.onResetRow(row)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
            >
              <BsArrowCounterclockwise className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </GridCellWrap>
  );
}

function StateCellRenderer(params: ICellRendererParams<ErrorCatalogRow>) {
  const row = params.data;
  const context = params.context as ErrorCatalogGridContext | undefined;
  if (!row || !context) return <EmptyGridCell />;

  const dirty = context.isDirty(row);

  if (dirty) {
    return (
      <GridCellWrap className="justify-end">
        <span className="inline-flex items-center rounded border border-[#b7791f]/40 bg-[#b7791f]/10 px-2 py-0.5 text-[11px] font-medium text-[#b7791f]">
          Unsaved
        </span>
      </GridCellWrap>
    );
  }

  if (row.id != null) {
    return (
      <GridCellWrap className="justify-end">
        <span className="inline-flex items-center rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
          Custom
        </span>
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap className="justify-end">
      <span className="inline-flex items-center rounded border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
        Default
      </span>
    </GridCellWrap>
  );
}

export function createErrorCatalogColumnDefs(): ColDef<ErrorCatalogRow>[] {
  return [
    {
      headerName: 'Acquirer error',
      field: 'errorCode',
      colId: 'errorCode',
      cellRenderer: ErrorCodeCellRenderer,
      flex: 1.2,
      minWidth: 200,
      cellClassRules: {
        'border-l-[3px] border-l-[#c69026]': (params) => {
          const context = params.context as ErrorCatalogGridContext | undefined;
          return Boolean(params.data && context?.isDirty(params.data));
        },
        'border-l-[3px] border-l-primary': (params) => {
          const context = params.context as ErrorCatalogGridContext | undefined;
          return Boolean(
            params.data
            && context
            && !context.isDirty(params.data)
            && params.data.id != null,
          );
        },
      },
    },
    {
      headerName: 'Category',
      field: 'category',
      colId: 'category',
      cellRenderer: CategoryCellRenderer,
      width: 120,
    },
    {
      headerName: 'Severity',
      field: 'severity',
      colId: 'severity',
      cellRenderer: SeverityCellRenderer,
      width: 120,
    },
    {
      headerName: 'Message shown to user',
      colId: 'displayMessage',
      cellRenderer: MessageEditorCellRenderer,
      flex: 2,
      minWidth: 280,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Hits',
      field: 'hits',
      colId: 'hits',
      width: 90,
      cellClass: 'text-right',
      valueFormatter: (params) => fmtHits(Number(params.value) || 0),
    },
    {
      headerName: 'Last modified',
      colId: 'lastModified',
      width: 130,
      valueGetter: (params) => params.data?.lastModified ?? null,
      valueFormatter: (params) => fmtDate(String(params.value ?? '')),
    },
    {
      headerName: 'State',
      colId: 'state',
      cellRenderer: StateCellRenderer,
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
    },
  ];
}
